import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Car, Mail, Megaphone, Wallet, Bot, CalendarClock, AlertTriangle, Users } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { formatPrice } from "@/lib/format";

function StatCard({ testid, icon: Icon, label, value, hint, onClick, accent = "electric" }) {
  const colors = {
    electric: "border-electric/30 text-electric",
    amber: "border-amber-400/30 text-amber-400",
    emerald: "border-emerald-500/30 text-emerald-400",
    red: "border-red-400/30 text-red-400",
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`glass rounded-2xl p-5 border text-left w-full transition-all hover:bg-white/[0.04] ${colors[accent] || colors.electric}`}
    >
      <div className="flex items-center gap-2 text-sm mb-2 opacity-90">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
        {label}
      </div>
      <div className="font-heading text-3xl font-light text-white">{value}</div>
      {hint && <p className="text-xs text-slate-500 mt-2">{hint}</p>}
    </button>
  );
}

export function AdminOverview({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/overview");
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visitorChart = useMemo(() => {
    return (data?.visitors_daily || []).map((d) => {
      const p = d.date.split("-");
      return {
        ...d,
        label: p.length === 3 ? `${p[1]}.${p[2]}.` : d.date,
      };
    });
  }, [data?.visitors_daily]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 text-electric animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <p className="text-slate-400 text-sm">
        Az áttekintő nem töltődött be — indítsd újra a backendet, majd frissítsd az oldalt.
      </p>
    );
  }

  const go = (section, sub) => onNavigate?.(section, sub);

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-500">Teendők</p>
        <h2 className="font-heading text-2xl font-light mt-1">Áttekintő</h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          testid="overview-pending-cars"
          icon={Car}
          label="Jóváhagyásra vár"
          value={data.pending_cars}
          hint="Eladói feltöltés — nem publikus"
          accent={data.pending_cars > 0 ? "amber" : "electric"}
          onClick={() => go("cars", "pending")}
        />
        <StatCard
          testid="overview-inquiries"
          icon={Mail}
          label="Beérkező levelek"
          value={data.inquiries_total}
          hint="Összes érdeklődés"
          onClick={() => go("inquiries")}
        />
        <StatCard
          testid="overview-ad-pending"
          icon={Megaphone}
          label="Meta reklám kérés"
          value={data.ad_requests_pending}
          hint="Függőben"
          accent={data.ad_requests_pending > 0 ? "amber" : "electric"}
          onClick={() => go("ads")}
        />
        <StatCard
          testid="overview-viewing-seller"
          icon={CalendarClock}
          label="Időpont — eladó válasz"
          value={data.viewings_pending_seller}
          hint={
            data.viewings_stale_seller_24h > 0
              ? `${data.viewings_stale_seller_24h} db 24+ órája vár`
              : "AI javaslat megerősítésre vár"
          }
          accent={data.viewings_stale_seller_24h > 0 ? "red" : "electric"}
          onClick={() => go("listing-support", "viewings")}
        />
        <StatCard
          testid="overview-viewing-buyer"
          icon={CalendarClock}
          label="Időpont — vevő válasz"
          value={data.viewings_pending_buyer}
          hint="Ellenjavaslat elfogadása"
          onClick={() => go("listing-support", "viewings")}
        />
        <StatCard
          testid="overview-ai-chats"
          icon={Bot}
          label="AI hirdetés chat"
          value={data.ai_conversations}
          hint="Beszélgetés AI üzenettel"
          onClick={() => go("listing-support", "ai")}
        />
        <StatCard
          testid="overview-admin-assistant"
          icon={Bot}
          label="Admin asszisztens"
          value="→"
          hint="Kérdés, jóváhagyás, kredit, weboldal"
          onClick={() => go("assistant")}
        />
      </div>

      <div>
        <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">Látogatottság</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="glass rounded-2xl p-5 border border-violet-500/25">
            <div className="flex items-center gap-2 text-sm text-violet-300 mb-2">
              <Users className="w-4 h-4" /> Online most
            </div>
            <div className="font-heading text-3xl text-white" data-testid="overview-visitors-online">
              {data.visitors_online_now ?? 0}
            </div>
            <p className="text-xs text-slate-500 mt-2">Utolsó ~3 percben aktív lap (nyilvános oldal, admin nélkül)</p>
          </div>
          <div className="glass rounded-2xl p-5 border border-white/10">
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
              <Users className="w-4 h-4" /> {data.month_label} — egyedi látogató
            </div>
            <div className="font-heading text-3xl text-white" data-testid="overview-visitors-month">
              {data.visitors_month_unique ?? 0}
            </div>
            <p className="text-xs text-slate-500 mt-2">Becsült egyedi böngésző / eszköz (hónap)</p>
          </div>
        </div>
        <div className="glass rounded-2xl border border-white/10 p-4">
          <h3 className="font-heading text-base mb-1">Napi egyedi látogatók (30 nap)</h3>
          <p className="text-xs text-slate-500 mb-4">Az elmúlt hónap forgalma naponta bontva</p>
          <div className="h-52 w-full" data-testid="overview-visitors-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={visitorChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 10 }} width={32} />
                <Tooltip
                  contentStyle={{ background: "#111318", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }}
                  formatter={(v) => [`${v} fő`, "Egyedi látogató"]}
                />
                <Area type="monotone" dataKey="unique_visitors" stroke="#8b5cf6" fill="rgba(139,92,246,0.25)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass rounded-2xl p-5 border border-white/10">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
            <Wallet className="w-4 h-4" /> Ma (Stripe)
          </div>
          <div className="font-heading text-xl text-white">{formatPrice(data.today_revenue_huf || 0)}</div>
        </div>
        <div className="glass rounded-2xl p-5 border border-emerald-500/20 sm:col-span-1">
          <p className="text-xs text-slate-500">{data.month_label} — bevétel</p>
          <p className="font-heading text-xl text-emerald-300 mt-1">{formatPrice(data.month_revenue_huf || 0)}</p>
        </div>
        <div className="glass rounded-2xl p-5 border border-red-500/20">
          <p className="text-xs text-slate-500">{data.month_label} — kiadás</p>
          <p className="font-heading text-xl text-red-300 mt-1">{formatPrice(data.month_expense_huf || 0)}</p>
        </div>
        <button
          type="button"
          onClick={() => go("finance")}
          className="glass rounded-2xl p-5 border border-electric/30 text-left hover:bg-white/[0.03]"
        >
          <p className="text-xs text-slate-500">{data.month_label} — profit</p>
          <p className={`font-heading text-xl mt-1 ${(data.month_profit_huf || 0) >= 0 ? "text-white" : "text-red-300"}`}>
            {formatPrice(data.month_profit_huf || 0)}
          </p>
          <p className="text-xs text-electric mt-2">Pénzügyek →</p>
        </button>
      </div>

      {(data.pending_cars > 0 || data.viewings_stale_seller_24h > 0) && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-400/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            {data.pending_cars > 0 && <p>{data.pending_cars} hirdetés jóváhagyásra vár.</p>}
            {data.viewings_stale_seller_24h > 0 && (
              <p>{data.viewings_stale_seller_24h} megtekintés-időpontnál az eladó 24 órája nem válaszolt.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
